'use client'

import resoneticsLogoUrl from '@/public/resonetics.svg'
import LockOutlined from '@mui/icons-material/LockOutlined'
import LoginRounded from '@mui/icons-material/LoginRounded'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import * as colors from '@mui/material/colors'
import Container from '@mui/material/Container'
import { useTheme } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Image from '@shared/components/SafeImage'
import { DISPLAY_JOB_FILTERS } from '@shared/config'
import { ROUTES } from '@shared/routes'
import { signIn, useSession } from 'next-auth/react'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'

export const Login: React.FC<{ enableE2eTests: boolean }> = ({ enableE2eTests }) => {
	const { status } = useSession()
	const isLoading = status === 'loading'
	const { palette } = useTheme()
	const query = useSearchParams()
	const error = query.get('error')

	const [testCredentials, setTestCredentials] = useState({ username: '', password: '' })
	const [isTestLoading, setIsTestLoading] = useState(false)
	const [testLoginError, setTestLoginError] = useState<string | null>(null)

	const handleTestLogin = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsTestLoading(true)
		setTestLoginError(null)

		const result = await signIn('test-credentials', {
			username: testCredentials.username,
			password: testCredentials.password,
			callbackUrl: ROUTES.home,
			redirect: false,
		})

		if (result?.ok) {
			window.location.assign(ROUTES.home)
			return
		}

		setTestLoginError('Invalid test credentials')
		setIsTestLoading(false)
	}

	return (
		<Container
			component='main'
			maxWidth='xs'
			sx={{ display: 'flex', mx: 'auto', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', mt: -7 }}
		>
			<Avatar sx={{ bgcolor: 'secondary.main', width: 60, height: 60 }}>
				<LockOutlined fontSize='large' />
			</Avatar>
			<Image src={resoneticsLogoUrl} height={150} width={600} alt='Resonetics' style={{ aspectRatio: 1 }} />
			<Button
				size='large'
				disabled={isLoading}
				onClick={e => {
					e.preventDefault()
					localStorage.setItem(DISPLAY_JOB_FILTERS, 'true')
					return signIn('azure-ad', { callbackUrl: ROUTES.home })
				}}
				fullWidth
				variant='contained'
				endIcon={<LoginRounded />}
				sx={{ bgcolor: isLoading ? palette.grey[500] : palette.primary.main }}
			>
				{isLoading ? <CircularProgress size={26} sx={{ color: palette.common.white }} /> : 'Sign In'}
			</Button>

			{/* Hidden login form for E2E testing purposes only */}
			{enableE2eTests && (
				<Box
					component='form'
					onSubmit={handleTestLogin}
					sx={{
						display: 'none',
						mt: 3,
						p: 2,
						border: '1px solid #ddd',
						borderRadius: 1,
						bgcolor: '#f9f9f9',
						width: '100%',
					}}
					data-testid='test-login-form'
				>
					<TextField
						id='test-username'
						data-testid='test-username'
						name='username'
						placeholder='Test Username'
						size='small'
						fullWidth
						value={testCredentials.username}
						onChange={e => setTestCredentials(prev => ({ ...prev, username: e.target.value }))}
						disabled={isTestLoading}
						sx={{ mb: 2 }}
					/>

					<TextField
						id='test-password'
						data-testid='test-password'
						name='password'
						type='password'
						placeholder='Test Password'
						size='small'
						fullWidth
						value={testCredentials.password}
						onChange={e => setTestCredentials(prev => ({ ...prev, password: e.target.value }))}
						disabled={isTestLoading}
						sx={{ mb: 2 }}
					/>

					<Button
						id='test-login-button'
						data-testid='test-login-button'
						type='submit'
						variant='outlined'
						size='small'
						disabled={isTestLoading}
						sx={{ width: 'auto' }}
					>
						Test Login
					</Button>
					{testLoginError && (
						<Typography color='error' variant='body2' sx={{ mt: 1 }}>
							{testLoginError}
						</Typography>
					)}
				</Box>
			)}

			{error ? (
				<Typography color='error' fontWeight={500} bgcolor={colors.yellow[100]} borderRadius={4} px={3} py={2} mt={3}>
					Error: {error}
				</Typography>
			) : null}
		</Container>
	)
}
